// The watch-driven drops of the tenergy.app token (owner 2026-10-24): one switch per group and the venues whose clients
// get no token. Here, on the tenergy.app page with the other drops, not in Network → Watch («этот раздел относится к
// рассылке»): the watch itself keeps every venue's buyers, these controls only decide who is sent the token.
import { useState } from "preact/hooks";
import { useResource } from "@tenergy/web-core/dashboard";
import { adminReads, adminWrites, type WatchDropGroup } from "../api/admin-api.js";
import { utc } from "../format.js";
import { Button, ErrorNote, Note, Panel, Pill, Spinner } from "../ui.js";

/**
 * The watch-driven drops' switch: on = every minute the watch's new buyers and clients (and their private activators)
 * get the token, the top decile from the Tier 0 fleet. Off by default; the worker also needs PROMO_WATCH_DROPS.
 */
/** The two groups, switched apart (owner 2026-10-24). */
const WATCH_DROP_GROUPS: ReadonlyArray<{ group: WatchDropGroup; label: string }> = [
  { group: "payers", label: "Payers: TRX sent to a venue's address" },
  { group: "clients", label: "Account clients: prepaid balance (API, bot, dashboard)" },
];

export function WatchDropsSwitch() {
  const res = useResource((signal) => {
    void signal;
    return adminReads.watchDrops();
  }, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const data = res.data;
  const [left, setLeft] = useState<string[] | null>(null);
  const saved = data?.exclude_venues ?? [];
  const venues = data?.venues ?? [];
  const chosen = left ?? saved;
  const all = [...new Set([...venues.map((v) => v.toLowerCase()), ...saved])].sort();
  const label = (v: string) => venues.find((x) => x.toLowerCase() === v) ?? v;
  const dirty = left !== null && [...left].sort().join() !== [...saved].sort().join();
  const run = async (write: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await write();
      setLeft(null);
      res.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "not saved");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel
      title="Sending to watch targets"
      subtitle="On: every minute, new buyers or clients found by the watch get the tenergy.app token, once each, and so do their private activators. The top decile goes from the Tier 0 senders, the rest from the main fleet. Both senders' bases and the worker's PROMO_WATCH_DROPS must be in place."
    >
      {data == null ? (
        res.error !== null ? <ErrorNote error={res.error} onRetry={() => res.reload()} /> : <Spinner />
      ) : (
        <div class="grid gap-3">
          <div class="grid gap-2">
            {WATCH_DROP_GROUPS.map(({ group, label: title }) => {
              const state = data[group];
              return (
                <div key={group} class="flex flex-wrap items-center gap-2">
                  <span class="w-[300px] text-[12.5px]">{title}</span>
                  <Pill value={state.enabled ? "on" : "off"} tone={state.enabled ? "good" : "neutral"} />
                  <Button
                    tone={state.enabled ? "ghost" : "primary"}
                    disabled={busy}
                    onClick={() => run(() => adminWrites.setWatchDrops({ group, enabled: !state.enabled }))}
                  >
                    {state.enabled ? "Switch off" : "Switch on"}
                  </Button>
                  <span class="text-[11.5px] text-ink-muted">
                    {state.updated_by === null ? "never switched" : `last switched ${utc(state.updated_at)}`}
                  </span>
                </div>
              );
            })}
          </div>
          <div>
            <p class="text-[12.5px]">Do not send the token to the clients of (both groups; they stay watched and counted in Network → Watch):</p>
            <div class="mt-1 flex flex-wrap gap-x-4 gap-y-1">
              {all.map((v) => (
                <label key={v} class="flex items-center gap-1.5 text-[12.5px]">
                  <input
                    class="size-4"
                    type="checkbox"
                    checked={chosen.includes(v)}
                    disabled={busy}
                    onChange={(event) => {
                      const on = (event.currentTarget as HTMLInputElement).checked;
                      setLeft(on ? [...chosen, v] : chosen.filter((x) => x !== v));
                    }}
                  />
                  {label(v)}
                </label>
              ))}
            </div>
            <div class="mt-2 flex items-center gap-2">
              <Button tone="primary" disabled={busy || !dirty} onClick={() => run(() => adminWrites.setWatchExcludeVenues({ exclude_venues: chosen }))}>
                Save
              </Button>
              {dirty ? <span class="text-[11.5px] text-ink-muted">not saved yet</span> : null}
            </div>
          </div>
        </div>
      )}
      {error === null ? null : <Note tone="warning">{error}</Note>}
    </Panel>
  );
}
